import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { stripePromise } from '../stripe';
import { PARENT_PLAN_PRICES, INVITE_PRICES } from '../utils/pricing';

const API_BASE = process.env.REACT_APP_API_URL || '';
const LOGO = '/dailyreps3.png';

const PLANS = {
  single: {
    name: 'One Player',
    subtitle: '',
    monthlyPrice: PARENT_PLAN_PRICES.individual.monthly,
    annualPrice: PARENT_PLAN_PRICES.individual.annualPerMonth,
    annualBilled: PARENT_PLAN_PRICES.individual.annualBilled,
    monthlyBilled: 'Billed monthly',
    plan: 'individual',
    features: [
      'Daily reps, auto-assigned',
      'Streaks, points, and shields',
      'Badges and a group of 20 leaderboard',
      'Full video drill library',
    ],
  },
  family: {
    name: 'Family',
    subtitle: 'For up to 8 players',
    monthlyPrice: PARENT_PLAN_PRICES.family.monthly,
    annualPrice: PARENT_PLAN_PRICES.family.annualPerMonth,
    annualBilled: PARENT_PLAN_PRICES.family.annualBilled,
    monthlyBilled: 'Billed monthly',
    plan: 'family',
    popular: true,
    features: [
      'Everything in One Player',
      'Add up to 8 players',
      'Each player gets their own account',
      'One simple family bill',
    ],
  },
};

export default function ParentOrder() {
  const location = useLocation();
  const navigate = useNavigate();
  const { code } = useParams(); // For /parents/join/:code
  const searchParams = new URLSearchParams(location.search);
  const planKey = searchParams.get('plan') || 'single';
  const isAnnual = location.pathname.includes('order-yearly');

  const [error, setError] = useState('');
  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState('');
  const [inviteInterval, setInviteInterval] = useState('annual');
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutError, setCheckoutError] = useState('');
  const [promoInfo, setPromoInfo] = useState(null);

  // Affiliate tracking
  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://affiliate-platform-production-f4fa.up.railway.app/track.js?pid=cmtdislyw024r2tx3gmysyb0g&v=1';
    s.async = true;
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  // Load invite code info
  useEffect(() => {
    if (!code) return;
    fetch(`${API_BASE}/api/invite-parent/${code}`)
      .then(r => {
        if (r.status === 410) throw new Error('This invite code has already been used.');
        if (!r.ok) throw new Error('Invalid invite code.');
        return r.json();
      })
      .then(data => setInviteInfo(data))
      .catch(err => setInviteError(err.message));
  }, [code]);

  // Load Stripe promo code info
  useEffect(() => {
    const promo = searchParams.get('promo');
    if (!promo || code) return;
    fetch(`${API_BASE}/api/promo/${encodeURIComponent(promo)}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setPromoInfo(data); })
      .catch(() => {});
  }, []); // eslint-disable-line

  const planData = code ? null : PLANS[planKey];
  const interval = code ? inviteInterval : (isAnnual ? 'annual' : 'monthly');
  const checkoutPlan = code ? (inviteInfo?.plan || 'parent_pays') : (planData?.plan || 'individual');
  const invitePrices = INVITE_PRICES[inviteInfo?.plan] || INVITE_PRICES.parent_pays;

  // Capture trialxyz param for testing shorter trials
  useEffect(() => {
    const t = searchParams.get('trialxyz');
    if (t) localStorage.setItem('trialxyz', t);
  }, []); // eslint-disable-line

  const fetchClientSecret = useCallback(async () => {
    const body = { plan: checkoutPlan, interval };
    if (code) body.invite_code = code;
    const aff = searchParams.get('aff');
    if (aff) body.affiliate_ref = aff;
    const t = localStorage.getItem('trialxyz');
    if (t) body.trial_days = parseInt(t, 10);
    const res = await fetch(`${API_BASE}/api/order/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Failed to start checkout');
    return data.client_secret;
  }, [checkoutPlan, interval, code]);

  const handleCheckout = async () => {
    setError('');
    setCheckoutError('');
    try {
      // Verify client secret can be fetched before showing checkout
      await fetchClientSecret();
      setShowCheckout(true);
    } catch (err) {
      setError(err.message);
    }
  };

  const toggleInterval = () => {
    setShowCheckout(false);
    if (code) {
      setInviteInterval(inviteInterval === 'annual' ? 'monthly' : 'annual');
    } else {
      const newPath = isAnnual ? '/parents/order-monthly' : '/parents/order-yearly';
      const t = searchParams.get('trialxyz');
      const extra = t ? `&trialxyz=${t}` : '';
      navigate(`${newPath}?plan=${planKey}${extra}`);
    }
  };

  // Invite code page
  if (code) {
    if (inviteError) {
      return (
        <div className="order-page">
          <header className="order-header"><div className="order-wrap">
            <div className="order-logo-row">
              <img src={LOGO} alt="Train Daily" className="order-logo" />
              <span className="order-brand">Train Daily</span>
            </div>
          </div></header>
          <main className="order-main"><div className="order-wrap">
            <div className="order-error-card">
              <h2>Invalid Invite</h2>
              <p>{inviteError}</p>
            </div>
          </div></main>
        </div>
      );
    }
    if (!inviteInfo) {
      return <div className="order-page"><div className="loading"><div className="spinner" /></div></div>;
    }

    // Club pays: free signup — redirect to onboarding without Stripe
    if (inviteInfo.plan === 'club_pays') {
      return (
        <div className="order-page">
          <header className="order-header"><div className="order-wrap">
            <div className="order-logo-row">
              <img src={LOGO} alt="Train Daily" className="order-logo" />
              <span className="order-brand">Train Daily</span>
            </div>
          </div></header>
          <main className="order-main"><div className="order-wrap">
            <p className="order-eyebrow">You're invited</p>
            <h1 className="order-title">Join {inviteInfo.team_name}</h1>
            <p className="order-lead">Your coach uses Train Daily for off-day training. Create a free account and your kid joins the team.</p>

            <div className="order-cards">
              <div className="order-card">
                <h3>Player</h3>
                <div className="order-card-subtitle">Join {inviteInfo.team_name}</div>
                <div className="order-price">Free</div>
                <div className="order-price-per">included with your club</div>
                <ul className="order-features">
                  <li><i className="fas fa-check"></i> Daily reps from your coach</li>
                  <li><i className="fas fa-check"></i> Streaks, points, and shields</li>
                  <li><i className="fas fa-check"></i> Team leaderboard</li>
                  <li><i className="fas fa-check"></i> Full video drill library</li>
                </ul>
                <button className="btn btn-primary order-btn" onClick={() => {
                  const affParam = searchParams.get('aff');
                  navigate(`/parents/onboarding?team_id=${inviteInfo.team_id}&plan=club_pays&team_name=${encodeURIComponent(inviteInfo.team_name)}${code ? '&code=' + encodeURIComponent(code) : ''}${affParam ? '&aff=' + affParam : ''}`);
                }}>
                  Create Account
                </button>
              </div>
            </div>

            <p className="order-fine">No credit card required. Your club covers the cost.</p>
          </div></main>
          <footer className="order-footer"><div className="order-wrap">
            <p>&copy; Train Daily. Built by Outsmart Press, Inc.</p>
          </div></footer>
        </div>
      );
    }

    // Scholarship: free signup with scholarship messaging
    if (inviteInfo.plan === 'scholarship') {
      return (
        <div className="order-page">
          <header className="order-header"><div className="order-wrap">
            <div className="order-logo-row">
              <img src={LOGO} alt="Train Daily" className="order-logo" />
              <span className="order-brand">Train Daily</span>
            </div>
          </div></header>
          <main className="order-main"><div className="order-wrap">
            <p className="order-eyebrow">You're invited</p>
            <h1 className="order-title">Join {inviteInfo.team_name}</h1>
            <p className="order-lead">You've been awarded a scholarship for Train Daily. Create your free account and your kid joins the team.</p>

            <div className="order-cards">
              <div className="order-card">
                <h3>Scholarship Player</h3>
                <div className="order-card-subtitle">Join {inviteInfo.team_name}</div>
                <div className="order-price">Free</div>
                <div className="order-price-per">scholarship</div>
                <ul className="order-features">
                  <li><i className="fas fa-check"></i> Daily reps from your coach</li>
                  <li><i className="fas fa-check"></i> Streaks, points, and shields</li>
                  <li><i className="fas fa-check"></i> Team leaderboard</li>
                  <li><i className="fas fa-check"></i> Full video drill library</li>
                </ul>
                <button className="btn btn-primary order-btn" onClick={() => {
                  const affParam = searchParams.get('aff');
                  navigate(`/parents/onboarding?team_id=${inviteInfo.team_id}&plan=scholarship&code=${code}&team_name=${encodeURIComponent(inviteInfo.team_name)}${affParam ? '&aff=' + affParam : ''}`);
                }}>
                  Create Account
                </button>
              </div>
            </div>

            <p className="order-fine">No credit card required. Your scholarship covers the cost.</p>
          </div></main>
          <footer className="order-footer"><div className="order-wrap">
            <p>&copy; Train Daily. Built by Outsmart Press, Inc.</p>
          </div></footer>
        </div>
      );
    }

    // Parent pays: Stripe checkout flow (with optional discount)
    const hasDiscount = !!inviteInfo.discount_label;
    const discountPercent = inviteInfo.discount_percent || 0;
    const applyDiscount = (priceStr) => {
      const num = parseFloat(priceStr.replace('$', ''));
      const discounted = num * (1 - discountPercent / 100);
      return '$' + discounted.toFixed(2);
    };

    return (
      <div className="order-page">
        <header className="order-header"><div className="order-wrap">
          <div className="order-logo-row">
            <img src={LOGO} alt="Train Daily" className="order-logo" />
            <span className="order-brand">Train Daily</span>
          </div>
        </div></header>
        <main className="order-main"><div className="order-wrap">
          <p className="order-eyebrow">You're invited</p>
          <h1 className="order-title">Join {inviteInfo.team_name}</h1>
          <p className="order-lead">Your coach uses Train Daily for off-day training. Sign up and your kid joins the team.</p>

          {!showCheckout && (
            <>
              <div className="order-toggle-wrap">
                <span className={`order-toggle-label ${inviteInterval !== 'annual' ? 'on' : ''}`}>Monthly</span>
                <div className={`order-switch ${inviteInterval === 'annual' ? 'annual' : ''}`} onClick={toggleInterval} role="button" tabIndex={0}></div>
                <span className={`order-toggle-label ${inviteInterval === 'annual' ? 'on' : ''}`}>Annual <span className="order-toggle-save">Save 40%</span></span>
              </div>

              <div className="order-cards">
                <div className="order-card">
                  <h3>Player</h3>
                  <div className="order-card-subtitle">Per player on {inviteInfo.team_name}</div>
                  {hasDiscount && (
                    <div className="order-discount-badge">
                      <i className="fas fa-tag"></i> {inviteInfo.discount_label}
                    </div>
                  )}
                  <div className="order-price">
                    {hasDiscount
                      ? applyDiscount(inviteInterval === 'annual' ? invitePrices.annualPerMonth : invitePrices.monthly)
                      : (inviteInterval === 'annual' ? invitePrices.annualPerMonth : invitePrices.monthly)}
                  </div>
                  <div className="order-price-per">per month</div>
                  {hasDiscount && (
                    <div className="order-price-original">
                      <s>{inviteInterval === 'annual' ? invitePrices.annualPerMonth : invitePrices.monthly}</s> regular price
                    </div>
                  )}
                  <div className="order-price-billed">{inviteInterval === 'annual' ? invitePrices.annualBilled : 'Billed monthly'}</div>
                  <ul className="order-features">
                    <li><i className="fas fa-check"></i> Daily reps from your coach</li>
                    <li><i className="fas fa-check"></i> Streaks, points, and shields</li>
                    <li><i className="fas fa-check"></i> Team leaderboard</li>
                    <li><i className="fas fa-check"></i> Full video drill library</li>
                  </ul>
                  {error && <div className="order-error">{error}</div>}
                  <button className="btn btn-primary order-btn" onClick={handleCheckout}>
                    Start your 30-day free trial
                  </button>
                </div>
              </div>

              <p className="order-fine">
                {hasDiscount
                  ? `30-day free trial, card required. After trial, ${inviteInfo.discount_label}.`
                  : '30-day free trial, card required. Cancel before day 30 and pay nothing.'}
              </p>
            </>
          )}

          {showCheckout && (
            <div className="order-checkout-embed">
              <button
                className="order-checkout-back"
                onClick={() => setShowCheckout(false)}
                type="button"
              >
                <i className="fas fa-arrow-left"></i> Back to pricing
              </button>
              <EmbeddedCheckoutProvider stripe={stripePromise} options={{ fetchClientSecret }}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div></main>
        <footer className="order-footer"><div className="order-wrap">
          <p>&copy; Train Daily. Built by Outsmart Press, Inc.</p>
        </div></footer>
      </div>
    );
  }

  // Regular parent order page
  if (!planData) {
    return <div className="order-page"><div className="order-wrap"><p>Invalid plan. Use ?plan=single or ?plan=family</p></div></div>;
  }

  const handleStartTrial = () => {
    const intervalParam = isAnnual ? 'annual' : 'monthly';
    const t = searchParams.get('trialxyz') || localStorage.getItem('trialxyz');
    const extra = t ? `&trialxyz=${t}` : '';
    const promo = searchParams.get('promo');
    const promoParam = promo ? `&promo=${encodeURIComponent(promo)}` : '';
    navigate(`/parents/onboarding?plan=${planData.plan}&interval=${intervalParam}${extra}${promoParam}`);
  };

  const hasPromo = !!promoInfo?.percent_off;
  const applyPromoDiscount = (priceStr) => {
    const num = parseFloat(priceStr.replace('$', ''));
    const discounted = num * (1 - promoInfo.percent_off / 100);
    return '$' + discounted.toFixed(2);
  };

  return (
    <div className="order-page">
      <header className="order-header"><div className="order-wrap">
        <div className="order-logo-row">
          <img src={LOGO} alt="Train Daily" className="order-logo" />
          <span className="order-brand">Train Daily</span>
        </div>
        <a href="https://www.traindaily.com/parents" className="order-nav-link">For Parents</a>
      </div></header>

      <main className="order-main"><div className="order-wrap">
        <p className="order-eyebrow">Get started free</p>
        <h1 className="order-title">
          {planData.name} <span className="accent">Plan</span>
        </h1>
        <p className="order-lead">
          Try your first drill free. No credit card required to start.
        </p>

        <div className="order-toggle-wrap">
          <span className={`order-toggle-label ${!isAnnual ? 'on' : ''}`}>Monthly</span>
          <div className={`order-switch ${isAnnual ? 'annual' : ''}`} onClick={toggleInterval} role="button" tabIndex={0}></div>
          <span className={`order-toggle-label ${isAnnual ? 'on' : ''}`}>Annual <span className="order-toggle-save">Save up to 37%</span></span>
        </div>

        <div className="order-cards">
          <div className={`order-card ${planData.popular ? 'popular' : ''}`}>
            {planData.popular && <div className="order-pop-badge">Best for families</div>}
            <h3>{planData.name}</h3>
            {planData.subtitle && <div className="order-card-subtitle">{planData.subtitle}</div>}
            {hasPromo && (
              <div className="order-discount-badge">
                <i className="fas fa-tag"></i> {promoInfo.label}
              </div>
            )}
            <div className="order-price">
              {hasPromo
                ? applyPromoDiscount(isAnnual ? planData.annualPrice : planData.monthlyPrice)
                : (isAnnual ? planData.annualPrice : planData.monthlyPrice)}
            </div>
            <div className="order-price-per">per month</div>
            {hasPromo && (
              <div className="order-price-original">
                <s>{isAnnual ? planData.annualPrice : planData.monthlyPrice}</s> regular price
              </div>
            )}
            <div className="order-price-billed">
              {hasPromo && isAnnual
                ? `${applyPromoDiscount(planData.annualBilled.split(',')[0])}, billed annually`
                : (isAnnual ? planData.annualBilled : planData.monthlyBilled)}
            </div>
            <ul className="order-features">
              {planData.features.map((f, i) => (
                <li key={i}><i className="fas fa-check"></i> {f}</li>
              ))}
            </ul>
            {error && <div className="order-error">{error}</div>}
            <button className="btn btn-primary order-btn" onClick={handleStartTrial}>
              Get Started Free
            </button>
          </div>
        </div>

        <p className="order-fine">
          {hasPromo
            ? `No credit card required. Try your first drill free, then start a 30-day trial. After trial, ${promoInfo.label}.`
            : 'No credit card required. Try your first drill free, then start a 30-day trial.'}
        </p>
      </div></main>

      <footer className="order-footer"><div className="order-wrap">
        <p>&copy; Train Daily. Built by Outsmart Press, Inc.</p>
      </div></footer>
    </div>
  );
}
